import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../config/api';
import {
  ShoppingCartIcon,
  ArrowPathIcon,
  DocumentArrowDownIcon,
  CheckIcon,
  ClipboardDocumentIcon,
} from '@heroicons/react/24/outline';

interface ShoppingListViewProps {
  planId?: number | null;
  onSelectPlan?: (planId: number) => void;
  coachMode?: boolean;
  clientId?: number;
}

export const ShoppingListView: React.FC<ShoppingListViewProps> = ({ planId, onSelectPlan, coachMode, clientId }) => {
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);
  const queryClient = useQueryClient();

  // Fetch plans to allow selection if no planId provided
  const { data: plansData } = useQuery({
    queryKey: coachMode ? ['meal-plans', 'client', clientId] : ['meal-plans'],
    queryFn: () => apiClient.getMealPlans(undefined, coachMode ? clientId : undefined),
    enabled: !planId,
  });

  // Fetch shopping list
  const { data: shoppingData, isLoading, error, refetch } = useQuery({
    queryKey: coachMode ? ['shopping-list', planId, 'client', clientId] : ['shopping-list', planId],
    queryFn: () => apiClient.getShoppingList(planId!, coachMode ? clientId : undefined),
    enabled: !!planId,
  });

  // Regenerate shopping list
  const regenerateMutation = useMutation({
    mutationFn: () => apiClient.regenerateShoppingList(planId!, coachMode ? clientId : undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: coachMode ? ['shopping-list', planId, 'client', clientId] : ['shopping-list', planId] });
    },
  });

  // Toggle item checked
  const toggleItemMutation = useMutation({
    mutationFn: ({ itemId, isChecked }: { itemId: number; isChecked: boolean }) =>
      apiClient.updateShoppingListItem(planId!, itemId, { is_checked: isChecked }, coachMode ? clientId : undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: coachMode ? ['shopping-list', planId, 'client', clientId] : ['shopping-list', planId] });
    },
  });

  // Export shopping list
  const handleExport = async (format: 'text' | 'markdown') => {
    try {
      const result = await apiClient.exportShoppingList(planId!, format, coachMode ? clientId : undefined);
      if (result.content) {
        // Create blob and download
        const blob = new Blob([result.content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `shopping-list.${format === 'markdown' ? 'md' : 'txt'}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  // Copy to clipboard
  const handleCopyToClipboard = async () => {
    try {
      const result = await apiClient.exportShoppingList(planId!, 'text', coachMode ? clientId : undefined);
      if (result.content) {
        await navigator.clipboard.writeText(result.content);
        setCopiedToClipboard(true);
        setTimeout(() => setCopiedToClipboard(false), 2000);
      }
    } catch (error) {
      console.error('Copy failed:', error);
    }
  };

  const categoryIcons: Record<string, string> = {
    produce: '🥬',
    dairy: '🥛',
    protein: '🥩',
    grains: '🌾',
    pantry: '🥫',
    frozen: '❄️',
    beverages: '🥤',
    other: '📦',
  };

  const categoryLabels: Record<string, string> = {
    produce: 'Produce',
    dairy: 'Dairy & Eggs',
    protein: 'Protein',
    grains: 'Grains & Bread',
    pantry: 'Pantry',
    frozen: 'Frozen',
    beverages: 'Beverages',
    other: 'Other',
  };

  // Show plan selector if no plan selected
  if (!planId) {
    const plans = plansData?.plans || [];
    const activePlan = plans.find((p: any) => p.status === 'active');

    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="text-center">
          <ShoppingCartIcon className="h-12 w-12 mx-auto text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">Select a Meal Plan</h3>
          <p className="mt-2 text-gray-500">
            Choose a meal plan to view its shopping list.
          </p>

          {plans.length > 0 ? (
            <div className="mt-6 space-y-2">
              {activePlan && (
                <button
                  onClick={() => onSelectPlan?.(activePlan.id)}
                  className="w-full px-4 py-3 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 text-left"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium text-green-900">
                        {activePlan.name || 'Active Plan'}
                      </span>
                      <span className="ml-2 text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded-full">
                        Active
                      </span>
                    </div>
                    <span className="text-sm text-green-600">{activePlan.start_date}</span>
                  </div>
                </button>
              )}

              {plans
                .filter((p: any) => p.id !== activePlan?.id)
                .slice(0, 5)
                .map((plan: any) => (
                  <button
                    key={plan.id}
                    onClick={() => onSelectPlan?.(plan.id)}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 text-left"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900">
                        {plan.name || `Plan: ${plan.start_date}`}
                      </span>
                      <span className="text-sm text-gray-500">{plan.status}</span>
                    </div>
                  </button>
                ))}
            </div>
          ) : (
            <p className="mt-4 text-sm text-gray-400">
              No meal plans available. Generate a plan first.
            </p>
          )}
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/3" />
          <div className="space-y-2">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-10 bg-gray-100 rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
        <p className="text-red-600">Failed to load shopping list</p>
        <button
          onClick={() => refetch()}
          className="mt-2 text-sm text-blue-600 hover:text-blue-800"
        >
          Try again
        </button>
      </div>
    );
  }

  const items = shoppingData?.items || [];
  const byCategory = shoppingData?.byCategory || {};
  const checkedCount = items.filter((item: any) => item.is_checked).length;
  const totalCount = items.length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShoppingCartIcon className="h-6 w-6 text-blue-600" />
            <div>
              <h2 className="font-semibold text-gray-900">Shopping List</h2>
              <p className="text-sm text-gray-500">
                {checkedCount} of {totalCount} items checked
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleCopyToClipboard}
              className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {copiedToClipboard ? (
                <>
                  <CheckIcon className="h-4 w-4 text-green-600" />
                  Copied!
                </>
              ) : (
                <>
                  <ClipboardDocumentIcon className="h-4 w-4" />
                  Copy
                </>
              )}
            </button>
            <button
              onClick={() => handleExport('text')}
              className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <DocumentArrowDownIcon className="h-4 w-4" />
              Export
            </button>
            <button
              onClick={() => regenerateMutation.mutate()}
              disabled={regenerateMutation.isPending}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
            >
              <ArrowPathIcon className={`h-4 w-4 ${regenerateMutation.isPending ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-4">
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-green-500 transition-all duration-300"
              style={{ width: `${totalCount > 0 ? (checkedCount / totalCount) * 100 : 0}%` }}
            />
          </div>
        </div>
      </div>

      {/* Categories */}
      {totalCount === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
          <ShoppingCartIcon className="h-12 w-12 mx-auto text-gray-300" />
          <p className="mt-4 text-gray-500">No items in shopping list</p>
          <p className="text-sm text-gray-400">
            Generate or refresh the list from your meal plan
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(byCategory).map(([category, categoryItems]: [string, any]) => (
            <div key={category} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                <h3 className="font-medium text-gray-900 flex items-center gap-2">
                  <span>{categoryIcons[category] || '📦'}</span>
                  {categoryLabels[category] || category}
                  <span className="text-xs text-gray-500 font-normal">
                    ({categoryItems.length})
                  </span>
                </h3>
              </div>

              <ul className="divide-y divide-gray-100">
                {categoryItems.map((item: any) => (
                  <li key={item.id} className="px-4 py-2">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={item.is_checked}
                        onChange={(e) => {
                          toggleItemMutation.mutate({
                            itemId: item.id,
                            isChecked: e.target.checked,
                          });
                        }}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className={`flex-1 text-sm ${item.is_checked ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                        {item.ingredient_name}
                      </span>
                      {item.quantity && (
                        <span className={`text-xs ${item.is_checked ? 'text-gray-300' : 'text-gray-500'}`}>
                          {item.quantity} {item.unit}
                        </span>
                      )}
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
